// @ts-strict-ignore
import { TFunction } from 'react-i18next'
import useDepositPaymentReference from 'pages/Wallets/components/Deposit/hooks/useDepositPaymentReference'
import { DepositAccountNumberFormats, DepositBankAccount, Options } from 'types/BankAccountsDetails'
import { Address } from 'types/beneficiaries'
import { Currencies, WalletType } from 'types/types'
import { WalletStatus } from 'types/wallets'
import { getAddress } from './beneficiaries'
import { WalletProperties } from './wallets/fiat'

const BANK_ACCOUNT_META_TRANSLATION_KEYS = {
  0: 'primary',
  1: 'secondary',
  2: 'tertiary',
}

export const getLocalBankDetails = (
  options: Options,
  wallet: WalletType,
  currency: string,
  isSegregated: boolean,
  t: TFunction<'translation', undefined>
) => {
  let details = {}

  switch (currency) {
    case 'EUR':
      details = getEurDetails(options, wallet, t)
      break
    case 'GBP':
      details = getGbpDetails(options, wallet, t)
      break
    case 'ZAR':
      details = getZarDetails(options, wallet, t)
      break
    case 'NGN':
      details = getNgnDetails(options, wallet, t)
      break
    case 'USD':
      details = getUsdDetails(options, wallet, t)
      break
  }

  if (isSegregated) {
    if (options?.reference) {
      details[t('referenceNumber')] = options.reference
    } else {
      delete details[t('referenceNumber')]
    }
  }

  return details
}

const getEurDetails = (options: Options, wallet, t: TFunction<'translation', undefined>) => {
  return {
    [t('referenceNumber')]: wallet.address || '-',
    ...(options?.accountHolderName && { [t('accountName')]: options.accountHolderName }),
    ...(options?.accountNumber && { [t('accountNumber')]: options.accountNumber }),
    ...(options?.bankCode && { [t('bankCode')]: options.bankCode }),
  }
}

const getGbpDetails = (options: Options, wallet, t: TFunction<'translation', undefined>) => {
  return {
    [t('referenceNumber')]: wallet.address || '-',
    ...(options?.accountHolderName && { [t('accountName')]: options.accountHolderName }),
    ...(options?.accountNumber && { [t('accountNumber')]: options.accountNumber }),
    ...(options?.bankCode && { [t('bankCode')]: options.bankCode }),
  }
}

const getZarDetails = (options: Options, wallet, t: TFunction<'translation', undefined>) => {
  return {
    [t('referenceNumber')]: wallet.address || '-',
    ...(options?.accountHolderName && { [t('accountName')]: options.accountHolderName }),
    ...(options?.accountNumber && { [t('accountNumber')]: options.accountNumber }),
    ...(options?.bankCode && { [t('bankCode')]: options.bankCode }),
    ...(options?.bankName && { [t('bankName')]: options.bankName }),
    ...(options?.accountType && { [t('accountType')]: options.accountType }),
  }
}

const getUsdDetails = (options: Options, wallet, t: TFunction<'translation', undefined>) => {
  const isCubixDepositDetails = options?.accountNumberFormat === 'CUBIX'
  return {
    [t('referenceNumber')]: wallet?.address || '-',
    ...(options?.accountHolderName &&
      isCubixDepositDetails && { [t('clientName')]: options.accountHolderName }),
    ...(options?.accountHolderName && { [t('accountName')]: options.accountHolderName }),
    ...(options?.usdBankRoutingNumber && {
      [t('bankRoutingNumber')]: options.usdBankRoutingNumber,
    }),
    ...(options?.accountNumber && { [t('accountNumber')]: options.accountNumber }),
    ...(options?.bankCode && {
      [t(isCubixDepositDetails ? 'bicSwiftCode' : 'abaRoutingNumber')]: options.bankCode,
    }),
    ...(options?.bankName && { [t('bankName')]: options.bankName }),
    ...(options?.bankCity && { [t('bankCity')]: options.bankCity }),
    ...(options?.bankState && { [t('bankState')]: options.bankState }),
    ...(options?.bankCountry && { [t('bankCountry')]: options.bankCountry }),
    ...(options?.bankPostalCode && { [t('bankPostalCode')]: options.bankPostalCode }),
  }
}

const getNgnDetails = (options: Options, wallet, t: TFunction<'translation', undefined>) => {
  return {
    [t('referenceNumber')]: wallet?.address || '-',
    ...(options?.accountHolderName && { [t('accountName')]: options.accountHolderName }),
    ...(options?.accountNumber && { [t('accountNumber')]: options.accountNumber }),
    ...(options?.bankName && { [t('bankName')]: options.bankName }),
  }
}

export const getV1SwiftBeneficiaryDetails = (
  options: Options,
  t: TFunction<'translation', undefined>
) => {
  return {
    ...(options?.beneficiaryName && { [t('beneficiaryName')]: options?.beneficiaryName }),
    ...(options?.beneficiaryAddress?.addressLine && {
      [t('beneficiaryAddress')]: options?.beneficiaryAddress?.addressLine,
    }),
    ...(options?.beneficiaryAddress?.city && {
      [t('beneficiaryCity')]: options?.beneficiaryAddress?.city,
    }),
    ...(options?.beneficiaryAddress?.countryCode && {
      [t('beneficiaryCountry')]: options?.beneficiaryAddress?.countryCode,
    }),
    ...(options?.beneficiaryAddress?.postalCode && {
      [t('beneficiaryZipCode')]: options?.beneficiaryAddress?.postalCode,
    }),
  }
}

export const getV2SwiftBeneficiaryDetails = (
  options: Options,
  t: TFunction<'translation', undefined>
) => {
  return {
    ...(options?.accountHolderName && { [t('accountHolderName')]: options?.accountHolderName }),
    ...(options?.beneficiaryAddress?.addressLine1 && {
      [t('accountHolderAddress')]: options?.beneficiaryAddress?.addressLine1,
    }),
    ...(options?.beneficiaryAddress?.city && {
      [t('beneficiaryCity')]: options?.beneficiaryAddress?.city,
    }),
    ...(options?.beneficiaryAddress?.countryCode && {
      [t('beneficiaryCountry')]: options?.beneficiaryAddress?.countryCode,
    }),
    ...(options?.beneficiaryAddress?.postCode && {
      [t('beneficiaryZipCode')]: options?.beneficiaryAddress?.postCode,
    }),
  }
}

export const useGetV1SwiftBankDetails = (
  options: Options,
  t: TFunction<'translation', undefined>,
  shouldFetchReference = true
) => {
  const paymentReference = useDepositPaymentReference(options)
  return {
    // EUR
    ...(options?.iban && { [t('accountNumber')]: options.iban }),
    ...(options?.bic && { [t('bic')]: options.bic }),
    // USD
    ...(options?.usdAccountNumber && { [t('accountNumber')]: options.usdAccountNumber }),
    ...(options?.usdBankRoutingNumber && {
      [t('abaRoutingNumber')]: options.usdBankRoutingNumber,
    }),
    ...(options?.bankName && { [t('bankName')]: options.bankName }),
    ...(options?.bankAddress?.addressLine && {
      [t('bankAddress')]: options.bankAddress.addressLine,
    }),
    ...(options?.bankAddress?.countryCode && {
      [t('bankCountry')]: options.bankAddress.countryCode,
    }),
    ...(shouldFetchReference && paymentReference && { [t('paymentReference')]: paymentReference }),
  }
}

export const getV2SwiftBankDetails = (
  options: Options,
  t: TFunction<'translation', undefined>,
  shouldFetchReference = true
) => {
  const paymentReference = useDepositPaymentReference(options)
  return {
    ...(options?.accountNumber && { [t('accountNumber')]: options.accountNumber }),
    ...(options?.bankCode && { [t('bic')]: options.bankCode }),
    ...(options?.usdBankRoutingNumber && {
      [t('abaRoutingNumber')]: options.usdBankRoutingNumber,
    }),
    ...(options?.bankName && { [t('bankName')]: options.bankName }),
    ...(options?.bankAddress?.addressLine1 && {
      [t('bankAddress')]: options.bankAddress.addressLine1,
    }),
    ...(options?.bankAddress?.countryCode && { [t('country')]: options.bankAddress.countryCode }),
    ...(options?.bankAddress?.postCode && { [t('bankZipCode')]: options?.bankAddress?.postCode }),
    ...(shouldFetchReference && paymentReference && { [t('remittanceInformation')]: paymentReference }),
  }
}

export const getSwiftBankDetails = (
  options: Options,
  t: TFunction<'translation', undefined>,
  shouldFetchReference = true
): {
  beneficiaryDetails: { [property: string]: string }
  beneficiaryBankDetails: { [property: string]: string }
} => {
  return {
    beneficiaryDetails: getV2SwiftBeneficiaryDetails(options, t),
    beneficiaryBankDetails: getV2SwiftBankDetails(options, t, shouldFetchReference),
  }
}

export const useGetSwiftDetails = (
  options: Options,
  t: TFunction<'translation', undefined>,
  shouldFetchReference = true
) => {
  const beneficiaryAddressData = {
    addressLine1: options?.beneficiaryAddress?.addressLine1,
    addressLine2: options?.beneficiaryAddress?.addressLine2,
    city: options?.beneficiaryAddress?.city,
    region: options?.bankAddress?.region,
    country: options?.beneficiaryAddress?.countryCode,
    postCode: options?.beneficiaryAddress?.postCode,
  }

  const bankAddressData: Address = {
    addressLine1: options?.bankAddress?.addressLine1,
    addressLine2: options?.bankAddress?.addressLine2,
    city: options?.bankAddress?.city,
    region: options?.bankAddress?.region,
    country: options?.bankAddress?.countryCode,
    postCode: options?.bankAddress?.postCode,
  }

  const beneficiaryAddress = getAddress(beneficiaryAddressData)
  const bankAddress = getAddress(bankAddressData)
  const paymentReference = useDepositPaymentReference(options)

  const nidLabelMap: Record<string, string> = {
    ROUTING_NUMBER: t('bankRoutingNumber'),
    SORT_CODE: t('bankSortCode'),
  }
  const nidLabel = nidLabelMap[options?.nid?.type]

  return {
    // Beneficiary details
    ...(options?.accountHolderName && { [t('accountHolderName')]: options?.accountHolderName }),
    ...(beneficiaryAddress && {
      [t('accountHolderAddress')]: beneficiaryAddress,
    }),
    // Bank details
    ...(options?.accountNumber && { [t('accountNumber')]: options.accountNumber }),
    ...(options?.bankCode && { [t('bic')]: options.bankCode }),
    ...(options?.usdBankRoutingNumber && {
      [t('abaRoutingNumber')]: options.usdBankRoutingNumber,
    }),
    ...(nidLabel && options?.nid?.value && { [nidLabel]: options.nid.value }),
    ...(options?.bankName && { [t('bankName')]: options.bankName }),
    ...(bankAddress && {
      [t('bankAddress')]: bankAddress,
    }),
    ...(shouldFetchReference && paymentReference && { [t('remittanceInformation')]: paymentReference }),
  }
}

export const getPaymentTitle = (
  account: DepositBankAccount,
  t: TFunction<'translation', undefined>,
  index?: number
): string => {
  const { currency, bankName } = account || {}

  if (account?.isSwift || account?.accountNumberFormat === DepositAccountNumberFormats.SWIFT) {
    return t('internationalTransfer', {
      label: BANK_ACCOUNT_META_TRANSLATION_KEYS[index] || currency,
    })
  } else if (account?.accountNumberFormat === DepositAccountNumberFormats.CUBIX) {
    return t('localCubixTransfer')
  } else {
    return account?.currency === Currencies.ZAR
      ? t('localTransferZAR', { bankName })
      : t('localTransfer', { label: BANK_ACCOUNT_META_TRANSLATION_KEYS[index] || currency })
  }
}

export const getWalletPropertiesForCapabilityChecks = (wallet: WalletType): WalletProperties => {
  return {
    currency: wallet?.currency?.code,
    supportsWithdrawals: wallet?.supportsWithdrawals,
    supportsThirdParty: wallet?.supportsThirdParty,
    supportsDeposits: wallet?.supportsDeposits,
    lsid: wallet?.lsid,
    isLegacyWallet: !wallet?.supportedTransferDestinations?.length,
  }
}

export const getIsWalletStatusSupported = (walletStatus: WalletStatus, isActiveOnly?: boolean) => {
  if (isActiveOnly) return walletStatus === WalletStatus.ACTIVE
  if (!walletStatus) return true
  return (
    walletStatus !== WalletStatus.INACTIVE &&
    walletStatus !== WalletStatus.SUSPENDED_BY_ACCOUNT_SUSPENSION
  )
}
